import { Tab, TabGroup, TabList } from '@headlessui/react'
import FunnelHeader from 'modules/funnels/funnel/components/funnel-header'
import FunnelStepsList from 'modules/funnels/funnel/components/funnel-steps-list'
import FunnelStepsListSkeleton from 'modules/funnels/funnel/components/funnel-steps-list-skeleton'
import { FunnelLoco } from 'modules/funnels/funnel/constants/funnel-loco'
import DeviceModeProvider from 'modules/funnels/funnel/context/device-mode-context'
import { FunnelStepSettingTabsEnum } from 'modules/funnels/funnel/enums/funnel-step-setting-tabs-enum'
import useContentScrollingSize from 'modules/funnels/funnel/hooks/use-content-scrolling-styles'
import { useFunnelStepQuery } from 'modules/funnels/funnel/hooks/use-funnel-step-query'
import { useFunnelStepsList } from 'modules/funnels/funnel/hooks/use-funnel-steps-list'
import { useUpdateFunnelStepQuery } from 'modules/funnels/funnel/hooks/use-update-funnel-step-query'
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface'
import { getFunnelSettingsTabList } from 'modules/funnels/funnel/utils/get-funnel-settings-tab-list'
import { getFunnelStepSettingTabContent } from 'modules/funnels/funnel/utils/get-funnel-step-setting-tab-content'
import { FunnelId } from 'modules/funnels/types/funnel-interface'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { updateFunnelStepInDB } from 'shared/db/utils/funnels'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import useMediaQuery from 'shared/hooks/use-media-query'

const Funnel: NextPage = () => {
  const router = useRouter()
  const { t } = useLocoTranslation()

  const funnelId = parseInt(router.query.funnelId as string) as FunnelId

  const { funnelStepsList, mutateFunnelStepsList } = useFunnelStepsList(funnelId)
  const { updateFunnelStepQuery, clearFunnelStepTabQuery } = useUpdateFunnelStepQuery()

  const { isXS: isMobile } = useMediaQuery()

  const { contentRef, contentHeight, listHeight, listRef } = useContentScrollingSize(isMobile)

  const [selectedFunnelStep, setSelectedFunnelStep] = useState<FunnelStepInterface | null>(null)
  const [selectedTab, setSelectedTab] = useState<FunnelStepSettingTabsEnum | null>(null)

  const settingsTabsList = getFunnelSettingsTabList(selectedFunnelStep?.type)

  function selectFunnelStep(funnelStep: FunnelStepInterface) {
    setSelectedFunnelStep(funnelStep)
    updateFunnelStepQuery(funnelStep.id)
    if (!isMobile) {
      setSelectedTab(FunnelStepSettingTabsEnum.stepConfiguration)
      updateFunnelStepQuery(funnelStep.id, FunnelStepSettingTabsEnum.stepConfiguration)
    }
    updateFunnelStepInDB(funnelId, funnelStep.id)
  }

  function selectFunnelStepTab(
    tabType: FunnelStepSettingTabsEnum,
    funnelStep: FunnelStepInterface | null,
  ) {
    if (funnelStep) {
      setSelectedTab(tabType)
      updateFunnelStepQuery(funnelStep.id, tabType)
    }
  }

  const isFunnelListIsEmpty = funnelStepsList && funnelStepsList.length === 0

  const resetSelectedMobileTab = () => {
    clearFunnelStepTabQuery()
    setSelectedTab(null)
  }

  const { selectedFunnelStepId, selectedFunnelStepTab } = useFunnelStepQuery({
    funnelStepsList,
    setSelectedFunnelStep,
  })

  useEffect(() => {
    if (funnelStepsList && Number(selectedFunnelStepId) !== selectedFunnelStep?.id) {
      const selectedFunnelStepEntity = funnelStepsList.find(
        funnelStep => funnelStep.id === Number(selectedFunnelStepId),
      )
      if (selectedFunnelStepEntity && selectedFunnelStepId) {
        setSelectedFunnelStep(selectedFunnelStepEntity)
        updateFunnelStepInDB(funnelId, selectedFunnelStepId)
        if (selectedFunnelStepTab) {
          setSelectedTab(selectedFunnelStepTab)
        } else if (isMobile) {
          setSelectedTab(null)
        } else {
          setSelectedTab(FunnelStepSettingTabsEnum.stepConfiguration)
        }
      }
    }
  }, [selectedFunnelStepId, funnelStepsList, isMobile])

  useEffect(() => {
    if (!isMobile && !selectedTab) {
      setSelectedTab(FunnelStepSettingTabsEnum.stepConfiguration)
    }
  }, [isMobile, selectedTab])

  useEffect(() => {
    if (window.location.hash) {
      const stepId = window.location.hash.replace('#step', '')
      updateFunnelStepQuery(Number(stepId))
    }
  }, [router])

  return (
    <DeviceModeProvider resetSelectedMobileTab={resetSelectedMobileTab} isMobile={isMobile}>
      <div className={`flex flex-col gap-5`}>
        <FunnelHeader funnelId={funnelId} selectedFunnelStep={selectedFunnelStep} />
        <div className="flex flex-col gap-3 lg:flex-row lg:gap-6">
          <div
            className={`relative w-full lg:min-h-fit lg:w-[330px] ${
              isMobile && selectedTab && !isFunnelListIsEmpty ? 'hidden' : ''
            }`}
          >
            {!funnelStepsList ? (
              <FunnelStepsListSkeleton />
            ) : (
              <FunnelStepsList
                funnelId={funnelId}
                funnelSteps={funnelStepsList}
                mutate={mutateFunnelStepsList}
                selectedFunnelStep={selectedFunnelStep}
                selectFunnelStep={selectFunnelStep}
                listHeight={listHeight}
                listRef={listRef}
              />
            )}
          </div>
          <div
            className={`bg-background-secondary customScroll flex h-full min-h-[200px] w-full flex-col items-stretch gap-5 overflow-x-hidden rounded-md`}
            style={
              contentHeight
                ? {
                    height: contentHeight,
                    overflowY: 'scroll',
                  }
                : {}
            }
          >
            <div className="flex flex-col gap-7 lg:gap-10" ref={contentRef}>
              <TabGroup>
                <TabList
                  className={`funnelCustomScroll flex flex-col gap-y-0 overflow-x-auto lg:flex-row ${
                    isMobile && selectedTab ? 'hidden' : ''
                  }`}
                >
                  {settingsTabsList.map(tab => (
                    <Tab
                      onClick={() => selectFunnelStepTab(tab.type, selectedFunnelStep)}
                      key={tab.type}
                      disabled={isFunnelListIsEmpty}
                      className={`${
                        tab.type === selectedTab
                          ? 'lg:text-text-primary lg:border-b-brand-primary lg:border-b-[3px]'
                          : 'lg:border-b-text-primary/15 lg:border-b-1'
                      } lg:main-transition-colors border-[rgb(var(--text-primary))]/15 w-full border-b-[1px] px-4 py-4 outline-hidden last:border-b-[0px] lg:w-fit lg:pb-[22px] lg:pt-[27px] lg:first:pl-8 lg:last:border-b-[1px]`}
                    >
                      <div className="flex gap-2">
                        {tab.iconRenderer(tab.type === selectedTab)}
                        <span className="font-inter whitespace-nowrap font-semibold sm:text-[15px]">
                          {t(tab.caption)}
                        </span>
                      </div>
                    </Tab>
                  ))}
                </TabList>
              </TabGroup>
              {getFunnelStepSettingTabContent(
                funnelId,
                selectedFunnelStep,
                selectedTab,
                isFunnelListIsEmpty,
              )}
            </div>
          </div>
        </div>
      </div>
    </DeviceModeProvider>
  )
}

export const getServerSideProps = getBaseServerSideProps(FunnelLoco, 'header.top_menu.funnels')

export default Funnel
